import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import Globals, { URLS } from "../../../utility/constants/appConstants";
import ServiceAgreement from "./serviceAgreements";
import NoServiceAgreement from "./noServiceAgreements";
import NoPaymentDue from "./noPaymentDue";
import RecentPayments from "./recentPayments";
import Loader from "../../loader";
import { AllAccountDetailsClient } from "../../../utility/apiClients/account/accountDetailsClient";
import {
  getDataFromLocalStorage,
  invoiceAmountDue,
} from "../../../utility/features/helpers/accountHelper";
import paymentsHistoryClient from "../../../utility/apiClients/account/paymentsHistoryClient";
import Storage from "../../../utility/features/helpers/storageHelper";
import tempJson from "../../../json/fallbackglobalvariables.json";
import accountSummaryCss from "../../../styles/myAccount/accountSummary.module.scss";
import Error from "../../errorMessage";

import { IsLoggedIn } from "../../../utility/features/helpers/authHelper";
import { setCustomerInfo } from "../../../reduxStore/login/loginSlice";
import customerSummaryClient from "../../../utility/apiClients/customer/customerSummaryClient";
import {
  setAccountDetails,
  setDisplayEasyPayButton,
  setDisplayPrePayButton,
} from "../../../reduxStore/myAccount/accountSummarySlice";
import AccountDetailsClient from "../../../utility/apiClients/account/accountDetailsClient";
import styles from "../../../styles/Payments/AccountPayments.module.scss";
import paymentStatusClient from "../../../utility/apiClients/payments/paymentStatusClient";
import missionStatusClient from "../../../utility/apiClients/user/missionStatusClient";

export default function AccountPayments(props) {
  
  const dispatch = useDispatch();
  const nav = useNavigate();
  const labels = tempJson.acc;
  const custNumbers = JSON.parse(localStorage.getItem("customerNumbers")) || [];
  const [pError, setPError] = useState("");
  
  const [allInvoices, setAllInvoices] = useState([]);
  const [isActiveCustomer, setIsActiveCustomer] = useState(true);
  
  const [addressByCust, setAddressByCust] = useState([]);
  const customerSummaryInfo = useSelector((state) => state.login.customerInfo);
  const [customerSummary, setCustomerSummary] = useState(customerSummaryInfo);
  const selectedServiceAddress = useSelector(
    (state) => state.accountSummary.selectedServiceAddress
  );
  const allCustomersApiCalled = useSelector((state) => state.login.allCustomersApiCalled);
  const [unauthorizeResponse, setUnauthorizeResponse] = useState(false);
  const [inActiveMessage, setInActiveMessage] = useState("");

  const [serviceAddress, customerNumber] = useMemo(() => {
    let custNumber = "";
    let serviceAddr = "";
    if (selectedServiceAddress != null) {
      [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
    } else {
      custNumber = customerSummary ? customerSummary[0].CustomerNumber : custNumbers[0];
      serviceAddr = customerSummary
        ? customerSummary[0].ServiceAddresses[0].address
        : "";
    }
    
    return [serviceAddr, custNumber];
  }, [selectedServiceAddress, customerSummary]);

  const [state, setState] = useState({
    serviceAgreements: null,
    inactiveCustomerInvoices: null,
    infoBlockload: false,
    infoBlockloadServices: null,
    queryString:
      typeof window !== "undefined"
        ? window.location.search !== ""
          ? window.location.search
          : ""
        : "",
    prePaydiscAmt: 0,
    manageEasyPay: 0,
    error: null,
  });

  const [invoiceStatuses, setInvoiceStatuses] = useState({
    processing: [],
    failed: [],
    success: [],
    not_found: [],
    onqueue: [],
    pending: []
  });

  const CustomerDetailsApiCallRef = useRef(customerNumber);
  const fetchAccountDetailsRef = useRef(false);
  const lastFetchedRef = useRef({ customerNumber: null, serviceAddress: null });

  const missionStatusCheck = async () => {
    try {
      const missionStatus = await missionStatusClient();
      return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
    } catch (err) {
      console.error("Error checking mission status api:", err);
      return false;
    }
  };

  useEffect(() => {
    if(CustomerDetailsApiCallRef.current != customerNumber) {
      CustomerDetailsApiCallRef.current = customerNumber;
      setCustomerSummary(null);
    }
  }, [customerNumber]);

  useEffect(() => {
    setPError("");
    setState(prev => ({
      ...prev, 
      serviceAgreements: null,
      inactiveCustomerInvoices: null,
      infoBlockloadServices: null
    }));

    if (customerSummary != null) {
      // Prevent duplicate calls
      if (fetchAccountDetailsRef.current) {
        return;
      }
      
      fetchAccountDetailsRef.current = true;
      lastFetchedRef.current = { customerNumber, serviceAddress };
      
      (async () => {
        if(allCustomersApiCalled){
          const isMissionAvailable = await missionStatusCheck();
          if (!isMissionAvailable) {
            setPError(Globals.acc.missionDownErrorDisplay);
            fetchAccountDetailsRef.current = false;
            return;
          }
          await fetchAccountDetails(customerNumber, serviceAddress);
          fetchAccountDetailsRef.current = false;
        }
      })();

      // payment history
      paymentsHistoryClient(customerNumber).then(
        (response) => {
          let selectedInvoiceData =
            response?.invoices?.filter((d) => d.invoiceBalance > 0) || [];
          setState((prev) => ({
            ...prev,
            inactiveCustomerInvoices: selectedInvoiceData,
            infoBlockload: true,
          }));
        },
        () => setState((prev) => ({ ...prev, infoBlockload: true }))
      );

      const timeout = setTimeout(() => {
        const modal = document.getElementById("LoadingAnimationModal");
        if (modal) modal.className = "closed";
      }, 3000);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "pageVirtualMetaData",
        page: {
          name: "Payments",
          type: "Information Page",
          section: "Account",
          customer_id:
            JSON.parse(localStorage.getItem("customerNumbers"))?.[0] || {},
          virtual: {
            path: window.location.pathname,
            referral: window.document.referrer,
          },
        },
      });

      return () => {
        clearTimeout(timeout);
        fetchAccountDetailsRef.current = false;
      };
    } else {
      (async () => {
        await getCustomerSummary(customerNumber);
      })();
    }
  }, [customerSummary, selectedServiceAddress, customerNumber, serviceAddress, allCustomersApiCalled]);

  const checkPaymentStatus = async () => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setPError(Globals.acc.missionDownErrorDisplay);
      return;
    }

    const allPaidInvoices = allInvoices.map(sa => ({ invoiceNumber: sa.invoiceNo, saNumber: sa.salesAgrNum }));

    try {
      const response = await paymentStatusClient(allPaidInvoices);

      if (response?.error) {
        setPError("Some error occurred.");
      } else if (response?.data) {
        // Process the payment status response
        const processing = [];
        const failed = [];
        const success = [];
        const not_found = [];
        const onqueue = [];
        const pending = [];

        response?.data.forEach(item => {
          const invoiceNumber = item.invoice_number;
          const status = item.status.toLowerCase();

          if (status === 'processing' || status === 'in-progress') {
            processing.push(invoiceNumber);
          } else if (status === 'failed') {
            failed.push(invoiceNumber);
          } else if (status === 'success') {
            success.push(invoiceNumber);
          } else if (status === 'not_found') {
            not_found.push(invoiceNumber);
          } else if (status === 'onqueue') {
            onqueue.push(invoiceNumber);
          } else if (status === 'pending') {
            pending.push(invoiceNumber);
          }
        });

        setInvoiceStatuses({ processing, failed, success, not_found, onqueue, pending });
      } else {
        setPError("Some error occurred.");
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPError("Some error occurred.");
    } finally { }
  };

  useEffect(() => {
    if (allInvoices.length > 0 || (state.serviceAgreements != null && state.serviceAgreements.length > 0)) {
      (async () => await checkPaymentStatus())();
    }
  }, [allInvoices, state.serviceAgreements]);

  const fetchAccountDetails = async (customerNumber, serviceAddress) => {
    setState((prev) => ({
      ...prev,
      infoBlockloadServices: false,
      infoBlockload: false,
      error: null,
    }));
    try {
      const response = await AccountDetailsClient(
        customerNumber,
        serviceAddress
      );
      if (
        response?.CODE === "ER" &&
        response?.DESCRIPTION === "NO ACTIVE CUSTOMER INFORMATION"
      ) {
        setIsActiveCustomer(false);
        setInActiveMessage(response.DESCRIPTION);
       
        return;
      }

      if (response && !response.errorMsg) {
        const accData = getDataFromLocalStorage(response);

        setState((prev) => ({
          ...prev,
          ...getDataFromLocalStorage(response, true),
          infoBlockloadServices: true,
          infoBlockload: true,
          error: null,
        }));

        let activeServiceAgreements = [];
        accData?.serviceAgreements.forEach((invoice) => {
          if (
            invoice.isBadSalesAgreement !== "N" &&
            invoiceAmountDue(invoice) > 0 &&
            invoice.inactiveReason !== "SSP" &&
            invoice.inactiveReason !== 'NRN'
          )
            return null;

          if (invoice?.Invoices.length > 0) {
            invoice?.Invoices.forEach((inv) => {
              if (inv.invoiceBalance > 0 && inv.invoiceOpen === "Y") {
                activeServiceAgreements.push({
                  payTotalPymt: inv.invoiceBalance,
                  salesAgrNum: inv?.salesAgrNum,
                  invoiceNo: inv?.invoiceNum,
                  invoiceAmt: inv?.invoiceBalance,
                  payBunit: invoice?.asgBunit,
                  svcLineDesc: inv?.svcServiceLine,
                  dueDate: inv?.invoiceDate,
                });
              }
            });
          }
        });

        const uniqueActiveServiceAgreements = activeServiceAgreements.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.invoiceNo === item.invoiceNo)
        );
        setAllInvoices(uniqueActiveServiceAgreements);

        dispatch(setAccountDetails(accData));
        dispatch(
          setDisplayPrePayButton(
            response?.SalesAgreementVer5PPEPList?.[0]?.dspPPSignupButton === "Y"
          )
        );
        dispatch(
          setDisplayEasyPayButton(
            response?.SalesAgreementVer5PPEPList?.[0]?.dspEPSignupButton === "Y"
          )
        );
        
      } else if (response?.errorStatus === 401) {
        setUnauthorizeResponse(true);
      } else if (response?.errorStatus === 424) {
        setPError("Some error occurred.");
      } else {
        throw response.errorMsg;
      }
    } catch (err) {
      if(err != "signal is aborted without reason"){
        setPError("Some error occurred.");
      }
    } 
  };

  const getCustomerSummary = async (custNum) => {
    if (custNum === "") return;

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setPError(Globals.acc.missionDownErrorDisplay);
      return;
    }

    //setPError("");
   
    try {
      const customerSummary = await customerSummaryClient(custNum);

      if (customerSummary?.status && customerSummary?.status === 401 ) {
        console.log("Unauthorized access - 401");
        localStorage.clear();
        nav("/login", { replace: true });
        return;
      }

      if (
        customerSummary?.data === null ||
        customerSummary?.data === undefined
      ) {
        setPError("Some error occurred.");
        return;
      }
      dispatch(setCustomerInfo(customerSummary?.data));
      setCustomerSummary([customerSummary?.data]);
      setAddressByCust(customerSummary?.data?.ServiceAddresses || []);
    } catch (err) {
      setPError("Some error occurred.");
    } finally {
      
    }
  };

  useEffect(() => {
    if (!IsLoggedIn() || unauthorizeResponse) {
      (async () => {
        await Storage.logoutUser();
        window.location = "/login";
      })();
    }
  }, [unauthorizeResponse]);

  const {
    serviceAgreements,
    prePaydiscAmt,
    manageEasyPay,
    inactiveCustomerInvoices,
    infoBlockload,
    infoBlockloadServices,
    queryString,
    error,
  } = state;

  return (
    <>
      <div className="bg-green d-md-none my-20 py-20 row d-md-none">
        <div className="col-12 gfw-3 glh-1_1 text-white tru-mma-mobile-current">
          <span className="d-block gfs-30 mb-6">Payments</span>
          <span className="d-block gfs-20">
            Pay your balance or view your payment history.
          </span>
        </div>
      </div>
      {pError != "" && <Error labels={labels} errMsg={pError} />}
      {pError == "" && infoBlockloadServices === true ? (<>
        <div
          className={`bg-gray-100 mb-40 p-md-30 mb-40 tru-mma-main-wrapper ${accountSummaryCss["tru-mma-main-wrapper--summary"]}`}
        >
          <div className="container-fluid">
            {manageEasyPay > 0 && (
              <div className="row">
                <div className="col text-center">
                  <span className={`align-items-center d-block ${accountSummaryCss["gfs-24"]} ${accountSummaryCss["gfw-3"]} ${accountSummaryCss["mb-20"]} ${accountSummaryCss["text-gray-med"]}`}>
                    <span>
                      <img
                        src="/images/MyAccount/Icons/exclamation-circle-solid.svg"
                        width="30"
                        height="30"
                      />
                      <span>{labels.haveServiceBanner}&nbsp;</span>
                      <span className={`${accountSummaryCss["font-italic"]}`}>
                        <strong className={`${accountSummaryCss["gfw-9"]}`}>{labels.easytext}</strong>
                        {labels.pay}
                        {labels.tooltipsmbl}
                      </span>
                    </span>
                    <Link
                      to={URLS.easyPaySettings + queryString}
                      className={`btn ${accountSummaryCss["bg-gray-300"]} ${accountSummaryCss["gfw-3"]} glh-1_8 mx-10 py-0 text-white`}
                    >
                      {labels.manageText}{" "}
                      <span className={`${accountSummaryCss["font-italic"]}`}>
                        <strong className={`${accountSummaryCss["gfw-9"]}`}>{labels.easytext}</strong>
                        {labels.pay}
                      </span>{" "}
                      {labels.settingsText}
                    </Link>
                  </span>
                </div>
              </div>
            )}
            {serviceAgreements != null && serviceAgreements.length > 0
              ? serviceAgreements.map((serviceAgreement, i) => (
                <React.Fragment key={`${serviceAgreement.Id}-${i}`}>
                  <ServiceAgreement
                    serviceAgreement={serviceAgreement}
                    sqftMapModalRef={""}
                    uri={window.location.pathname}
                    labels={labels}
                    paymentsCss={accountSummaryCss}
                    invoiceStatuses={invoiceStatuses}
                  />
                </React.Fragment>
              ))
              : serviceAgreements != null && serviceAgreements.length === 0 &&
                inactiveCustomerInvoices != null && inactiveCustomerInvoices.length > 0
                ? inactiveCustomerInvoices.map((inactiveCustomerInvoice, i) => (
                  <React.Fragment
                    key={`${inactiveCustomerInvoice.salesAgrNum}-${i}`}
                  >
                    <NoServiceAgreement
                      inactiveCustomerInvoice={inactiveCustomerInvoice}
                      labels={labels}
                      paymentsCss={accountSummaryCss}
                      invoiceStatuses={invoiceStatuses}
                    />
                  </React.Fragment>
                ))
                : serviceAgreements != null && serviceAgreements.length === 0 &&
                inactiveCustomerInvoices != null && inactiveCustomerInvoices.length === 0 &&
                infoBlockload && <NoPaymentDue labels={labels} />}
          </div>
        </div>
        <div className={`row ${accountSummaryCss["font-italic"]}`}>
          <div className={`col-12 col-md-5 ${accountSummaryCss["mob-px-0"]}`}>
            {((Array.isArray(prePaydiscAmt) && prePaydiscAmt.length > 0) || (!Array.isArray(prePaydiscAmt) && !!prePaydiscAmt > 0)) && (
              <Link
                to="/my-account/payments/prepay-payment"
                className={styles.prepayBanner}
              >
                <div className={styles.topSection}>
                  <span className={styles.prepayText}>
                    <strong>{labels.pre}</strong>
                    {labels.pay}&nbsp;
                  </span>
                  {labels.preAmountBannerPayment}
                  <span className={styles.discountAmount}>
                    {parseFloat(prePaydiscAmt).toFixed(2)}
                  </span>
                </div>
              </Link>
            )}
          </div>
          <div className={`col-12 col-md-7 ms-md-auto ${manageEasyPay > 0 ? accountSummaryCss["auto-pay-btn"] : ""}`}>
            {manageEasyPay > 0 && (
              <Link to={URLS.easyPayPayment + queryString} className="">
                <div className="glh-1_2 px-20 py-10" style={{ flex: "1 1 100%" }}>
                  {labels.epayBannerText}
                  <span className={`${accountSummaryCss["font-italic"]}`}>
                    <strong className="gfw-9">{labels.easytext}</strong>
                    {labels.pay}
                  </span>
                  .
                </div>
                <div className={`align-items-center ${accountSummaryCss["bg-green"]} d-flex gfs-14 justify-content-center text-center`}>
                  {labels.enrollEasypayText}
                </div>
              </Link>
            )}
          </div>
        </div> <br />
        {serviceAgreements != null && serviceAgreements.length > 0 && <RecentPayments labels={labels} />}
      </>
      ) : (
        pError == "" && <Loader />
      )}
    </>
  );
}
